import React, { useState, useEffect, useCallback } from 'react';
import { StoreOverageFee, StoreOverageFeeCreate, OverageFeeType, FeeFrequency, OVERAGE_FEE_TYPE_LABELS, FEE_FREQUENCY_LABELS } from '../../../types/site';
import { siteService } from '../../../services/siteService';
import { authService } from '../../../services/auth/authService';
import { formatCurrency } from '../../../utils/formatters';
import { formatCategoryName, EffectiveFeeCategory, feeCategoryService } from '../../../services/feeCategoryService';

// Mapping from fee_type to fee category name
const FEE_TYPE_TO_CATEGORY: Record<string, string> = {
  adult: 'occupancy_fee',
  child: 'occupancy_fee',
  pet: 'pet_fee',
  vehicle: 'vehicle_fee',
};

const FEE_TYPE_OPTIONS: { value: OverageFeeType; label: string }[] = [
  { value: 'adult', label: 'Adult' },
  { value: 'child', label: 'Child' },
  { value: 'pet', label: 'Pet' },
  { value: 'vehicle', label: 'Vehicle' },
];

const FREQUENCY_OPTIONS: { value: FeeFrequency; label: string }[] = [
  { value: 'nightly', label: 'Per Night' },
  { value: 'once', label: 'One-time (Per Booking)' },
  { value: 'weekly', label: 'Per Week' },
];

interface OverageFeeFormData {
  fee_type: OverageFeeType;
  base_included: number;
  max_allowed: number;
  amount: number;
  flat: boolean;
  frequency: FeeFrequency;
  active: boolean;
}

const StoreOverageFeesCard: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [overageFees, setOverageFees] = useState<StoreOverageFee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingFee, setEditingFee] = useState<StoreOverageFee | null>(null);
  const [formData, setFormData] = useState<OverageFeeFormData>({
    fee_type: 'adult',
    base_included: 2,
    max_allowed: 6,
    amount: 10,
    flat: true,
    frequency: 'nightly',
    active: true,
  });
  const [saving, setSaving] = useState(false);
  const [feeCategories, setFeeCategories] = useState<EffectiveFeeCategory[]>([]);

  const storeId = authService.getStoreId();

  const loadFeeCategories = useCallback(async () => {
    if (!storeId) return;
    try {
      const categories = await feeCategoryService.getEffectiveFeeCategories(storeId, { activeOnly: true });
      setFeeCategories(categories);
    } catch (err) {
      console.error('Failed to load fee categories:', err);
    }
  }, [storeId]);

  const loadOverageFees = useCallback(async () => {
    try {
      setLoading(true);
      const fees = await siteService.getStoreOverageFees();
      setOverageFees(fees);
      setError(null);
    } catch (err) {
      setError('Failed to load overage fees');
      console.error('Error loading store overage fees:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverageFees();
    loadFeeCategories();
  }, [loadOverageFees, loadFeeCategories]);

  const getFeeTypeLabel = (feeType: string): string => {
    return OVERAGE_FEE_TYPE_LABELS[feeType as keyof typeof OVERAGE_FEE_TYPE_LABELS] || feeType;
  };

  const getFrequencyLabel = (frequency: string): string => {
    return FEE_FREQUENCY_LABELS[frequency as keyof typeof FEE_FREQUENCY_LABELS] || frequency;
  };

  const getFeeTypeIcon = (feeType: string): string => {
    switch (feeType) {
      case 'adult': return '👤';
      case 'child': return '👶';
      case 'pet': return '🐕';
      case 'vehicle': return '🚗';
      default: return '📦';
    }
  };

  const getCategoryForFeeType = (feeType: string): string => {
    const categoryName = FEE_TYPE_TO_CATEGORY[feeType];
    return categoryName ? formatCategoryName(categoryName) : 'Unknown';
  };

  const handleDelete = async (feeId: number, feeType: string) => {
    const typeName = getFeeTypeLabel(feeType);
    if (!window.confirm(`Are you sure you want to delete the "${typeName}" overage fee? This cannot be undone.`)) {
      return;
    }
    try {
      await siteService.deleteStoreOverageFee(feeId);
      await loadOverageFees();
    } catch (err) {
      console.error('Error deleting overage fee:', err);
      alert('Failed to delete overage fee.');
    }
  };

  const openAddModal = () => {
    setEditingFee(null);
    // Find fee types that are not already configured
    const existingTypes = overageFees.map(f => f.fee_type);
    const availableTypes = FEE_TYPE_OPTIONS.filter(opt => !existingTypes.includes(opt.value));
    const defaultType: OverageFeeType = availableTypes.length > 0 ? availableTypes[0].value : 'adult';

    setFormData({
      fee_type: defaultType,
      base_included: 2,
      max_allowed: 6,
      amount: 10,
      flat: true,
      frequency: 'nightly',
      active: true,
    });
    setShowModal(true);
  };

  const openEditModal = (fee: StoreOverageFee) => {
    setEditingFee(fee);
    setFormData({
      fee_type: fee.fee_type,
      base_included: fee.base_included,
      max_allowed: fee.max_allowed,
      amount: fee.amount,
      flat: fee.flat,
      frequency: fee.frequency,
      active: fee.active,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (formData.base_included < 0) {
      alert('Base included must be 0 or greater');
      return;
    }
    if (formData.max_allowed < formData.base_included) {
      alert('Max allowed must be greater than or equal to base included');
      return;
    }
    if (formData.amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    setSaving(true);
    try {
      if (editingFee) {
        // Update existing fee
        await siteService.updateStoreOverageFee(editingFee.id, {
          base_included: formData.base_included,
          max_allowed: formData.max_allowed,
          amount: formData.amount,
          flat: formData.flat,
          frequency: formData.frequency,
          active: formData.active,
        });
      } else {
        // Create new fee - get the fee_category_id based on fee_type
        const feeCategoryId = await feeCategoryService.getCategoryIdForOverageFeeType(
          formData.fee_type,
          feeCategories
        );
        if (!feeCategoryId) {
          alert('Could not find fee category for this fee type. Please ensure fee categories are configured.');
          setSaving(false);
          return;
        }
        const createData: StoreOverageFeeCreate = {
          store_id: storeId,
          fee_type: formData.fee_type,
          fee_category_id: feeCategoryId,
          base_included: formData.base_included,
          max_allowed: formData.max_allowed,
          amount: formData.amount,
          flat: formData.flat,
          frequency: formData.frequency,
          active: formData.active,
        };
        await siteService.createStoreOverageFee(createData);
      }
      await loadOverageFees();
      setShowModal(false);
    } catch (err) {
      console.error('Error saving overage fee:', err);
      alert(err instanceof Error ? err.message : 'Failed to save overage fee');
    } finally {
      setSaving(false);
    }
  };

  // Get available fee types (exclude already configured ones when adding)
  const getAvailableFeeTypes = () => {
    if (editingFee) {
      // When editing, only show the current fee type
      return FEE_TYPE_OPTIONS.filter(opt => opt.value === editingFee.fee_type);
    }
    // When adding, exclude already configured types
    const existingTypes = overageFees.map(f => f.fee_type);
    return FEE_TYPE_OPTIONS.filter(opt => !existingTypes.includes(opt.value));
  };

  return (
    <div className="config-card">
      <div
        className="config-card-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="config-card-title">
          <span className="config-card-icon">💰</span>
          <h2>Overage Fees</h2>
        </div>
        <div className="config-card-header-right">
          <span className="config-card-db-label">campground_db/store_overage_fees</span>
          <span className={`config-card-toggle ${isExpanded ? 'expanded' : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="config-card-content">
          <p className="config-section-description">
            Default occupancy limits and fees. Sites can override these settings.
          </p>

          {loading ? (
            <div className="config-loading">Loading overage fees...</div>
          ) : error ? (
            <div className="config-error">{error}</div>
          ) : overageFees.length === 0 ? (
            <div className="config-empty">
              <p>No overage fees configured. Set limits for extra guests, pets, and vehicles.</p>
              <button className="btn btn-primary btn-sm" onClick={openAddModal}>+ Add Overage Fee</button>
            </div>
          ) : (
            <>
              <table className="config-table">
                <thead>
                  <tr>
                    <th>Fee Type</th>
                    <th>Category</th>
                    <th>Included</th>
                    <th>Max Allowed</th>
                    <th>Extra Fee</th>
                    <th>Frequency</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {overageFees.map(fee => (
                    <tr key={fee.id}>
                      <td>
                        <span className="fee-type-cell">
                          <span className="fee-type-icon">{getFeeTypeIcon(fee.fee_type)}</span>
                          {getFeeTypeLabel(fee.fee_type)}
                        </span>
                      </td>
                      <td>{getCategoryForFeeType(fee.fee_type)}</td>
                      <td>{fee.base_included}</td>
                      <td>{fee.max_allowed}</td>
                      <td className="amount">
                        {fee.flat
                          ? formatCurrency(fee.amount)
                          : `${fee.amount}%`
                        }
                      </td>
                      <td>{getFrequencyLabel(fee.frequency)}</td>
                      <td className={fee.active ? 'status-active' : 'status-inactive'}>
                        {fee.active ? 'Active' : 'Inactive'}
                      </td>
                      <td>
                        <div className="config-actions">
                          <button
                            className="btn btn-text btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(fee);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-text btn-sm btn-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(fee.id, fee.fee_type);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="config-card-footer">
                <button className="btn btn-primary btn-sm" onClick={openAddModal}>+ Add Overage Fee</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingFee ? 'Edit Overage Fee' : 'Add Overage Fee'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                {String.fromCharCode(215)}
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Fee Type *</label>
                  <select
                    value={formData.fee_type}
                    onChange={(e) => setFormData({ ...formData, fee_type: e.target.value as OverageFeeType })}
                    disabled={!!editingFee}
                  >
                    {getAvailableFeeTypes().map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  {editingFee && (
                    <small className="form-hint">Fee type cannot be changed after creation</small>
                  )}
                </div>
                <div className="form-group">
                  <label>Category (Auto-assigned)</label>
                  <input
                    type="text"
                    value={getCategoryForFeeType(formData.fee_type)}
                    disabled
                    readOnly
                  />
                  <small className="form-hint">Category is based on fee type</small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Base Included *</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.base_included}
                    onChange={(e) => setFormData({ ...formData, base_included: parseInt(e.target.value) || 0 })}
                  />
                  <small className="form-hint">Number included in base price</small>
                </div>
                <div className="form-group">
                  <label>Max Allowed *</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.max_allowed}
                    onChange={(e) => setFormData({ ...formData, max_allowed: parseInt(e.target.value) || 0 })}
                  />
                  <small className="form-hint">Maximum number allowed</small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Amount Type *</label>
                  <select
                    value={formData.flat ? 'flat' : 'percentage'}
                    onChange={(e) => setFormData({ ...formData, flat: e.target.value === 'flat' })}
                  >
                    <option value="flat">Flat Amount ($)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                    placeholder={formData.flat ? '10.00' : '5'}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Frequency *</label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value as FeeFrequency })}
                >
                  {FREQUENCY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreOverageFeesCard;
